import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

const COLORS = [
  { value: "blue", label: "Azul" },
  { value: "green", label: "Verde" },
  { value: "purple", label: "Roxo" },
  { value: "orange", label: "Laranja" },
  { value: "red", label: "Vermelho" },
  { value: "yellow", label: "Amarelo" },
];

interface AddEntityTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function AddEntityTypeDialog({ open, onOpenChange, onSuccess }: AddEntityTypeDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    label: "",
    color: "blue",
  });

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.label.trim()) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha nome técnico e label de exibição.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from("entity_types")
        .insert([formData]);

      if (error) throw error;

      toast({
        title: "Tipo criado",
        description: `Tipo "${formData.label}" criado com sucesso.`,
      });

      setFormData({ name: "", label: "", color: "blue" });
      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error creating entity type:", error);
      
      let errorMessage = "Não foi possível criar o tipo.";
      if (error.code === '23505') {
        errorMessage = "Já existe um tipo com esse nome técnico.";
      } else if (error.code === '42501') {
        errorMessage = "Você não tem permissão para criar tipos de empresa.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Erro",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Novo Tipo de Empresa</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome Técnico *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
              placeholder="ex: franquia"
            />
            <p className="text-xs text-muted-foreground">Usado internamente no sistema</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="label">Nome de Exibição *</Label>
            <Input
              id="label"
              value={formData.label}
              onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              placeholder="ex: Franquia"
            />
            <p className="text-xs text-muted-foreground">Mostrado ao usuário</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="color">Cor do Badge</Label>
            <Select
              value={formData.color}
              onValueChange={(value) => setFormData({ ...formData, color: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COLORS.map((color) => (
                  <SelectItem key={color.value} value={color.value}>
                    {color.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Criar Tipo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
